<template>
  <div class="space-y-6">
    <div>
      <h3 class="text-lg font-medium mb-2">
        {{ t("settings.organizationMembers") }}
      </h3>
      <p class="text-sm text-muted-foreground">
        {{ t("settings.organizationMembersDescription") }}
      </p>
    </div>

    <!-- Invite member button -->
    <div class="flex justify-start">
      <SettingsMemberInviteDialog @invited="handleMemberInvited">
        <Button variant="outline">
          <UserPlus class="h-4 w-4 mr-2" />
          {{ t("settings.inviteMember.title") }}
        </Button>
      </SettingsMemberInviteDialog>
    </div>

    <div class="space-y-3">
      <div v-if="pending" class="text-center py-4">
        <LoadingIndicator class="h-6 w-6 mx-auto text-muted-foreground" />
        <p class="text-sm text-muted-foreground mt-2">
          {{ t("common.loading") }}
        </p>
      </div>

      <div v-else-if="error" class="text-center py-4 text-red-600">
        <AlertCircle class="h-6 w-6 mx-auto mb-2" />
        <p class="text-sm">{{ t("settings.membersLoadError") }}</p>
      </div>

      <div
        v-else-if="!members?.length"
        class="text-center py-8 text-muted-foreground"
      >
        <Users class="h-12 w-12 mx-auto mb-4 opacity-50" />
        <p>{{ t("settings.noMembers") }}</p>
      </div>

      <div v-else class="space-y-3">
        <div
          v-for="member in members"
          :key="member.id"
          class="flex items-center justify-between p-4 border rounded-lg"
        >
          <div class="flex items-center space-x-3">
            <!-- Avatar -->

            <Avatar
              class="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden"
            >
              <AvatarImage
                v-if="member.image"
                :src="member.image"
                :alt="member.name || member.email"
                class="h-full w-full object-cover"
              />
              <AvatarFallback
                v-else-if="member.name"
                class="h-5 w-5 text-primary"
              >
                {{ member.name?.slice(0, 2) }}
              </AvatarFallback>
              <AvatarFallback v-else class="h-5 w-5 text-primary">
                <User class="h-5 w-5" />
              </AvatarFallback>
            </Avatar>

            <!-- Member info -->
            <div>
              <div class="font-medium">
                {{ member.name || member.email }}
              </div>

              <div v-if="member.invitationPending" class="flex items-center">
                <Badge
                  variant="outline"
                  class="text-xs text-amber-600 border-amber-300"
                >
                  <ClockFading class="h-3 w-3 mr-1" />
                  {{ t("settings.invitationPending") }}
                </Badge>
              </div>
              <div v-else class="text-sm text-muted-foreground">
                {{ member.email }}
              </div>
            </div>
          </div>

          <!-- Role management and Remove/Leave action -->
          <div class="flex items-center space-x-2">
            <!-- Role select for other users or current user role display -->
            <SettingsMemberRoleSelect
              :member="member"
              :can-change-role="canChangeRole(member)"
              @role-changed="handleRoleChanged"
            />

            <!-- Remove/Leave button -->
            <SettingsMemberRemoveDialog
              v-if="canRemoveMember(member)"
              :member="member"
              :is-leaving-myself="isLeavingMyself(member)"
              :is-last-admin="isLastAdmin(member)"
              @confirmed="handleMemberRemoval"
            >
              <Button
                variant="ghost"
                size="sm"
                :class="[
                  'text-muted-foreground hover:text-foreground',
                  isLastAdmin(member) ? 'opacity-50 cursor-not-allowed' : ''
                ]"
                :disabled="isLastAdmin(member)"
              >
                <LogOut v-if="isLeavingMyself(member)" class="h-4 w-4 mr-1" />
                <UserMinus v-else class="h-4 w-4 mr-1" />
                <span class="sr-only">
                  {{
                    isLeavingMyself(member)
                      ? t("settings.removeMember.leave.title")
                      : t("settings.removeMember.remove.title")
                  }}
                </span>
              </Button>
            </SettingsMemberRemoveDialog>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useApiOrganizationMembers, removeMember } from "~/composables/api";
import { successToast, errorToast } from "~/components/ui/toast";
import type { ApiOrganizationMember } from "~/server/api/organizations/[organizationId]/members/index.get";
import {
  AlertCircle,
  ClockFading,
  LogOut,
  User,
  UserMinus,
  UserPlus,
  Users
} from "lucide-vue-next";

const { user } = useUserSession();
const {
  data: members,
  status,
  error,
  refresh
} = await useApiOrganizationMembers();
const pending = computed(() => status.value === "pending");
const { t } = useI18n();

const handleMemberInvited = () => {
  refresh();
};

const handleRoleChanged = (updatedMember: ApiOrganizationMember) => {
  if (members.value) {
    const index = members.value.findIndex((m) => m.id === updatedMember.id);
    if (index !== -1) {
      members.value[index] = updatedMember;
    }
  }
};

const canChangeRole = (member: ApiOrganizationMember): boolean => {
  if (!user.value?.id || !members.value) return false;

  const currentUser = members.value.find((m) => m.id === user.value!.id);
  if (!currentUser) return false;

  // Cannot change your own role
  if (member.id === user.value!.id) return false;

  // Only admins can change roles of other users
  return currentUser.role === "ADMIN";
};

const canRemoveMember = (member: ApiOrganizationMember): boolean => {
  if (!user.value?.id || !members.value) return false;

  const currentUser = members.value.find((m) => m.id === user.value!.id);
  if (!currentUser) return false;

  // Can leave yourself
  if (member.id === user.value!.id) return true;

  // Only admins can remove others
  return currentUser.role === "ADMIN";
};

const isLeavingMyself = (member: ApiOrganizationMember): boolean => {
  return member.id === user.value?.id;
};

const isLastAdmin = (member: ApiOrganizationMember): boolean => {
  if (!members.value || member.role !== "ADMIN") return false;

  const adminCount = members.value.filter((m) => m.role === "ADMIN").length;
  return adminCount === 1;
};

const handleMemberRemoval = async (member: ApiOrganizationMember) => {
  try {
    await removeMember(member.id.toString());

    const isLeaving = isLeavingMyself(member);
    const memberName = member.name || member.email;

    // Show success toast
    successToast({
      title: isLeaving
        ? t("settings.removeMember.leave.success.title")
        : t("settings.removeMember.remove.success.title"),
      description: isLeaving
        ? t("settings.removeMember.leave.success.description")
        : t("settings.removeMember.remove.success.description", {
            name: memberName
          })
    });

    await refresh();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    console.error("Failed to remove member:", error);

    const isLeaving = isLeavingMyself(member);

    // Show error toast
    errorToast({
      title: isLeaving
        ? t("settings.removeMember.leave.error.title")
        : t("settings.removeMember.remove.error.title"),
      description:
        error?.data?.statusMessage ||
        (isLeaving
          ? t("settings.removeMember.leave.error.description")
          : t("settings.removeMember.remove.error.description"))
    });
  }
};
</script>
